import { zustandImmerMiddleware } from '@sketchnote/shared-components';
import { formatISO } from 'date-fns';
import { isNil } from 'lodash';
import { useKanbanStore } from 'src/features/kanban';
import { useTableStore } from 'src/features/list/store';
import { useAgileStore } from 'src/features/scrum/store';
import { v4 as uuid } from 'uuid';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import useTasksService, {
  fetchEpicTask,
  toggleWatchTask as toggleWatchTaskApi,
  updateSubTaskIndex,
  updateSubtaskParent,
} from '../api/useTasksService';
import { TasksStore } from '../types/tasks';
import { taskApprovalSlice } from './slices/createTaskApprovalSlice';

const {
  toggleUpovteTask,
  fetchTask,
  updateTaskProperties,
  addComment,
  deleteComment,
  updateComment,
  addFile,
  removeFile,
  markTaskAsComplete,
  createSubTask,
  deleteSubTask,
  shareTask,
  fetchTaskTimelog,
  addCustomField,
  deleteCustomField,
  deleteCustomTemplateField,
  updateCustomFieldName,
  updateCustomTemplateFieldName,
  onUpdateCustomFieldType,
  updateCustomTemplateFieldType,
  updateCustomFieldData,
  updateCustomTemplateFieldData,
  addCustomTemplateField,
  duplicateTaskTemplate,
  saveTemplate,
  importTemplate,
  updateTemplate,
  reorderCustomField,
} = useTasksService();

export const initialState = {
  tasks: {},
  taskTemplates: {},
  currentTaskId: undefined,
  isFullScreen: false,
  didTaskTemplateChange: false,
};

export const useTasksStore = create<
  TasksStore,
  [['zustand/devtools', TasksStore], ['zustand/immer', TasksStore]]
>(
  devtools(
    zustandImmerMiddleware((set, get) => ({
      ...initialState,
      resetTask: () => set(() => initialState),
      onAddSubTask: async ({ title, parentTaskId }) => {
        set((state) => {
          const { tasks } = state;
          const task = tasks[parentTaskId];
          if (task) {
            task.subtasks = task.subtasks ?? [];
            task.subtasks.push({
              name: [{ children: [{ text: title }] }],
              id: uuid(),
              type: 'task',
              isLoading: true,
            });
          }
        });
        await createSubTask(parentTaskId, [{ children: [{ text: title }] }]);
        // TODO: Refactor this to update the state locally without fetching the task again
        get().fetchTask(parentTaskId);
      },
      onDeleteSubTask: async ({ parentTaskId, taskId }) => {
        set((state) => {
          const { tasks } = state;
          const task = tasks[parentTaskId];
          if (task) {
            task.subtasks = task.subtasks ?? [];
            task.subtasks = task.subtasks.filter((obj) => obj.id !== taskId);
            if (task.subtaskCount !== undefined) {
              task.subtaskCount = task.subtasks.length;
            }
          }
        });
        await deleteSubTask(taskId);
        // TODO: Refactor this to update the state locally without fetching the task again
        get().fetchTask(parentTaskId);
      },
      onReorderSubTask: async ({ parentTaskId, oldIndex, newIndex }) => {
        let taskId: string | undefined;
        const ogTask = get().tasks[parentTaskId];
        set((state) => {
          const { tasks } = state;
          const task = tasks[parentTaskId];
          if (task) {
            task.subtasks = task.subtasks ?? [];
            const subtask = task.subtasks[oldIndex];
            if (subtask && subtask.id) {
              taskId = subtask.id;
              const [item] = task.subtasks.splice(oldIndex, 1);
              task.subtasks.splice(newIndex, 0, { ...item, subtaskIndex: newIndex });
            }
          }
        });
        if (!taskId) return false;
        const success = !!(await updateSubTaskIndex(taskId, newIndex));
        if (!success) {
          set((state) => {
            state.tasks[parentTaskId] = ogTask;
          });
        }
        return success;
      },
      onReorderCustomField: async ({ taskId, oldIndex, newIndex, taskTemplateRef }) => {
        const { tasks } = get();
        const task = tasks[taskId];
        if (task) {
          const fields = Array.isArray(task.fields) ? [...task.fields] : [];
          const field = fields.splice(oldIndex, 1)[0];
          fields.splice(newIndex, 0, { ...field, index: newIndex });
          set((state) => {
            state.tasks[taskId] = { ...task, fields };
          });
          if (field && field.fieldId) {
            const success = !!(await reorderCustomField(
              taskId,
              field.fieldId,
              newIndex,
              taskTemplateRef
            ));
            if (!success) {
              set((state) => {
                state.tasks[taskId] = { ...task };
              });
            }
            return success;
          }
        }
        return false;
      },

      /*added "taskTemplateRef" in all custom field APIs for storing custom fields against template id 
      in case of task templates and against board id in case of regular tasks.
      */
      onAddCustomField: async ({ taskId, boardId, taskTemplateRef, type, name, data, index }) => {
        const task = get().tasks[taskId];
        if (!task) return false;
        const fieldId = uuid();
        set((state) => {
          const { tasks } = state;
          const task = tasks[taskId];
          if (task) {
            if (!task.fields) {
              task.fields = [];
            }
            task.fields.push({
              fieldId: fieldId,
              type: type,
              label: name,
              data: data,
              index: index,
            });
          }
        });
        const response = !!(await addCustomField(
          taskId,
          fieldId,
          type,
          name,
          data,
          index,
          boardId,
          taskTemplateRef
        ));
        if (!response) {
          set((state) => {
            state.tasks[taskId] = { ...task };
          });
        }
        return !!response;
      },
      onAddCustomTemplateField: async ({ fieldId, taskTemplateRef, type, name, data }) => {
        const response = await addCustomTemplateField(fieldId, type, name, data, taskTemplateRef);
        return response;
      },

      onUpdateCustomFieldName: async ({ taskId, fieldId, name, taskTemplateRef }) => {
        if (!taskId) return false;
        const task = get().tasks[taskId];
        if (!task) return false;
        const fieldIndex = task.fields?.findIndex((item) => item.fieldId === fieldId);
        if (isNil(fieldIndex) || fieldIndex === -1) return false;
        const fieldName = task.fields?.[fieldIndex].label;
        set((state) => {
          state.tasks[taskId] = {
            ...task,
            fields: [...(task.fields || [])].map((f) =>
              f.fieldId === fieldId ? { ...f, label: name } : f
            ),
          };
        });
        const success = !!(await updateCustomFieldName(taskId, fieldId, name, taskTemplateRef));
        if (!success) {
          set((state) => {
            state.tasks[taskId] = {
              ...task,
              fields: task.fields,
            };
          });
        }
        return success;
      },
      onUpdateCustomTemplateFieldName: async ({ fieldId, name, taskTemplateRef }) => {
        set((state) => {
          const { tasks } = state;
          let template;
          taskTemplateRef && (template = tasks[taskTemplateRef]);
          if (template) {
            if (!template.fields) {
              template.fields = [];
            }
            for (const field of template.fields) {
              if (field.fieldId === fieldId) {
                field.label = name;
              }
            }
          }
        });
        const response = await updateCustomTemplateFieldName(fieldId, name, taskTemplateRef);
        return response;
      },

      onUpdateCustomFieldType: async ({ taskId, fieldId, type, name, taskTemplateRef }) => {
        set(async (state) => {
          const { tasks } = state;
          let task;
          taskId && (task = tasks[taskId]);
          if (task) {
            if (!task.fields) {
              task.fields = [];
            }
            const localIndex = task?.fields?.length || 0;
            const field = task.fields.find((item) => item.fieldId === fieldId);
            field && type && (field.type = type);
            field && name && (field.name = name);
            const response = await onUpdateCustomFieldType(
              taskId,
              fieldId,
              name,
              type,
              taskTemplateRef
            );
            get().fetchTask(taskId);
          }
        });
      },
      onUpdateCustomTemplateFieldType: async ({ fieldId, type, name, taskTemplateRef }) => {
        set((state) => {
          const { tasks } = state;
          let template;
          taskTemplateRef && (template = tasks[taskTemplateRef]);
          if (template) {
            if (!template.fields) {
              template.fields = [];
            }
            // const localIndex = template?.fields?.length || 0;
            for (const field of template.fields) {
              if (field.fieldId === fieldId) {
                field.label = name;
                field.type = type;
              }
            }
          }
        });
        const response = await updateCustomTemplateFieldType(fieldId, name, type, taskTemplateRef);
        return response;
      },

      onUpdateCustomFieldData: async ({
        taskId,
        fieldId,
        data,
        added,
        taskTemplateRef,
        callback,
      }) => {
        const ogTask = get().tasks[taskId];
        if (!ogTask) return false;
        set((state) => {
          const { tasks } = state;
          let task;
          taskId && (task = tasks[taskId]);
          if (task) {
            if (!task.fields) {
              task.fields = [];
            }
            const field = task.fields.find((item) => item.fieldId === fieldId);
            field && data && (field.data = data);
          }
        });
        const success = !!(await updateCustomFieldData(
          taskId,
          fieldId,
          data,
          added,
          taskTemplateRef
        ));
        if (!success) {
          if (taskId) {
            set((state) => {
              if (taskId) state.tasks[taskId] = { ...ogTask };
            });
          }
          return success;
        }
        callback && callback();
        return success;
      },

      onUpdateCustomTemplateFieldData: async ({ fieldId, data, added, taskTemplateRef }) => {
        set((state) => {
          const { tasks } = state;
          let template;
          taskTemplateRef && (template = tasks[taskTemplateRef]);
          if (template) {
            if (!template.fields) {
              template.fields = [];
            }
            const field = template.fields.find((item) => item.fieldId === fieldId);
            field && data && (field.data = data);
          }
        });
        const success = !!(await updateCustomTemplateFieldData(
          fieldId,
          data,
          added,
          taskTemplateRef
        ));
        return !!success;
      },

      onDeleteCustomField: async ({ taskId, fieldId, taskTemplateRef }) => {
        const task = get().tasks[taskId];
        if (!task) return false;
        set((state) => {
          const { tasks } = state;
          const task = tasks[taskId];
          if (task) {
            task.fields = task.fields ?? [];
            task.fields = task.fields.filter((obj) => obj.fieldId !== fieldId);
          }
        });
        const success = !!(await deleteCustomField(taskId, fieldId, taskTemplateRef));
        if (!success) {
          set((state) => {
            state.tasks[taskId] = { ...task };
          });
        }
        return success;
      },

      onDeleteCustomTemplateField: async ({ taskId, fieldId, taskTemplateRef }) => {
        set((state) => {
          const { tasks } = state;
          const template = taskTemplateRef && tasks[taskTemplateRef];
          if (template) {
            template.fields = template.fields ?? [];
            template.fields = template.fields.filter((obj) => obj.fieldId !== fieldId);
          }
        });
        const response = await deleteCustomTemplateField(taskId, fieldId, taskTemplateRef);
        return response;
      },

      onMarkAsCompleteSubTask: async ({ parentTaskId, taskId, completed = true }) => {
        set((state) => {
          const { tasks } = state;
          const task = tasks[parentTaskId];
          if (task) {
            task.subtasks = task.subtasks ?? [];
            const index = task.subtasks.findIndex((obj) => obj.id === taskId);
            task.subtasks[index].completed = completed;
          }
        });
        await markTaskAsComplete(taskId, completed);
      },
      fetchTask: async (id: string) => {
        set((state) => {
          const { tasks } = state;
          if (!tasks[id]) {
            tasks[id] = { isLoading: true };
          } else {
            tasks[id] = { ...tasks[id], isLoading: true };
          }
        });
        try {
          const response = await fetchTask({ id });
          if (response) {
            set((state) => {
              state.tasks[id] = { ...response, isLoading: false };
              state.setTask(id, { ...response, isLoading: false });
            });

            // if (response.workspaceId) {
            // const membersWithAccess = await useMembersT(response.workspaceId);

            // if (membersWithAccess?.data?.data) {
            //   set((state) => {
            //     const { tasks } = state;
            //     const task = tasks[id];
            //     if (task) {
            //       task.members = membersWithAccess?.data?.data;
            //     }
            //   });
            // }
            // }
          } else {
            set((state) => {
              state.tasks[id] = { isLoading: false };
              state.setTask(id, { isLoading: false });
            });
          }
        } catch (error) {
          console.error(error);
        }
      },
      fetchEpicTask: async (id: string) => {
        set((state) => {
          const { tasks } = state;
          if (!tasks[id]) {
            tasks[id] = { isLoading: true };
          } else {
            tasks[id] = { ...tasks[id], isLoading: true };
          }
        });
        try {
          const response = await fetchEpicTask({ id });
          if (response) {
            set((state) => {
              state.tasks[id] = { ...response, isLoading: false };
              state.setTask(id, { ...response, isLoading: false });
            });
            useAgileStore.setState({ currentEpicItems: response.items });
          } else {
            set((state) => {
              state.tasks[id] = { isLoading: false };
              state.setTask(id, { isLoading: false });
            });
          }
        } catch (error) {
          console.error(error);
        }
      },
      setTask: (taskid, task) => {
        set((state) => {
          state.tasks[taskid] = task;
          state.sendTaskDetailsToParent?.({ task });
        });
      },
      unsetTemplate: (taskid) => {
        set((state) => {
          delete state.tasks[taskid];
        });
      },
      onToggleTaskUpvote: async ({ taskId, isUpvote }) => {
        const ogTask = get().tasks[taskId];
        if (!ogTask) return false;
        set((state) => {
          const { tasks } = state;
          const task = tasks[taskId];
          if (task) {
            const vote = task.vote || 0;
            task.vote = isUpvote ? vote + 1 : vote - 1;
          }
        });
        const success = !!(await toggleUpovteTask(taskId, isUpvote));
        if (!success) {
          set((state) => {
            state.tasks[taskId] = ogTask;
          });
        }
        return success;
      },
      onMarkAsComplete: async ({ completed = true, taskId }) => {
        set((state) => {
          const { tasks } = state;
          const task = tasks[taskId];
          if (task) {
            task.completed = completed;
          }
        });
        const success = !!(await markTaskAsComplete(taskId, completed));
        if (!success) {
          set((state) => {
            const { tasks } = state;
            const task = tasks[taskId];
            if (task) {
              task.completed = !completed;
            }
          });
        }
        return success;
      },

      onFetchTaskTimelog: async ({ taskId }: { taskId: string }) => {
        const timeLog = await fetchTaskTimelog(taskId);

        set((state) => {
          const { tasks } = state;
          const task = tasks[taskId];

          if (task) {
            task.timeLog = timeLog;
          }
        });
      },

      onUpdateItemField: async ({ item, keys }) => {
        if (!item.id) return;
        const ogItem = get().tasks[item.id];
        set((state) => {
          if (item.id) {
            keys.map((key) => {
              const itemId = item.id;
              if (itemId && state.tasks[itemId]) {
                const task = state.tasks[itemId];
                if (task) {
                  task[key] = item[key];
                }
              }
            });
          }
        });

        const payload: any = {};

        keys.map((field) => {
          if (item[field] === undefined) {
            payload[field] = null;
          } else if (field === 'assignee') {
            payload[field] = item[field]?.map((obj) => obj.memberId);
          } else if (field === 'duplicate') {
            payload[field] = item[field]?.map((obj) => obj.memberId);
          } else if (field === 'assigner') {
            payload[field] = item.assigner;
          } else if (field === 'assignDate') {
            if (item?.assignDate?.length && item?.assignDate?.length > 1) {
              payload.start_date = item?.assignDate?.[0];
              payload.end_date = item?.assignDate?.[1];
            } else {
              payload.end_date = item?.assignDate?.[0] || null;
              payload.start_date = null;
            }
          } else if (field === 'estimates') {
            payload[field] = item[field]?.map((i) => {
              if (i.type === 'member') {
                return Object.assign({}, i, { member: i?.member });
              } else return i;
            });
          } else if (field === 'recurring') {
            //disable used only in FE to set respective payload for delete recurring data
            if (item[field]?.disable) {
              payload[field] = {
                task: item?.recurring?.recurringId,
              };
            } else {
              payload[field] = {
                task: item.id,
                event_type: 'create',
                recurring_type: item[field]?.type,
                recur_on: item[field]?.repeats,
                recur_periodic: { [item[field]?.custom as string]: item[field]?.interval },
                // ...{item[field]?.recurringId  && {recId: item[field]?.recurringId}  },
              };
            }
          } else if (field === 'reminderOffset') {
            payload[field] = {
              task: item.id,
              event_type: 'reminder',
              fire_at: item[field],
            };
          } else if (field === 'startDate') {
            payload.start_date = item.startDate;
          } else if (field === 'endDate') {
            payload.end_date = item.endDate;
          } else {
            payload[field] = item[field];
          }
        });
        payload.id = item.id;

        if (item.id) {
          const success = !!(await updateTaskProperties({ properties: payload, taskId: item.id }));
          if (!success) {
            set((state) => {
              if (item.id) state.tasks[item.id] = { ...ogItem };
            });
          }

          return success;
        }
        return false;
      },
      onAddComment: async ({ comment, boardId, itemId, tags, text, member }) => {
        const newComment = {
          id: uuid(),
          data: comment,
          createdAt: formatISO(new Date()),
          member: member,
        };
        set((state) => {
          const { tasks } = state;
          const task = tasks[itemId];
          if (task) {
            const comments = task.comments || [];
            comments.unshift(newComment);
            task.comments = comments;
          }
        });
        const success = !!(await addComment({
          boardId: boardId,
          content: comment,
          itemId: itemId,
          text: text,
          tags: tags,
          uuid: newComment.id,
        }));
        if (!success) {
          set((state) => {
            const { tasks } = state;
            const task = tasks[itemId];
            if (task) {
              const comments = task.comments || [];
              const commentIndex = comments.findIndex((comment) => comment.id === newComment.id);
              if (commentIndex > -1) {
                comments.splice(commentIndex, 1);
                task.comments = comments;
              }
            }
          });
        }
        return success;
      },
      onDeleteComment: async ({ commentId, itemId }) => {
        const ogTask = get().tasks[itemId];
        if (!ogTask) return false;

        set((state) => {
          const { tasks } = state;
          const task = tasks[itemId];
          if (task) {
            const comments = task.comments || [];
            const commentIndex = comments.findIndex((comment) => comment.id === commentId);
            if (commentIndex > -1) {
              comments.splice(commentIndex, 1);
              task.comments = comments;
            }
          }
        });
        const success = !!(await deleteComment({ commentId }));
        if (!success) {
          set((state) => {
            state.tasks[itemId] = ogTask;
          });
        }
        return success;
      },
      onUpdateComment: async ({ commentId, comment, tags, text, itemId }) => {
        const ogTask = get().tasks[itemId];
        if (!ogTask) return false;
        set((state) => {
          const { tasks } = state;
          const task = tasks[itemId];
          if (task) {
            const comments = task.comments || [];
            const commentIndex = comments.findIndex((comment) => comment.id === commentId);
            if (commentIndex > -1) {
              comments[commentIndex].data = comment;
              task.comments = comments;
            }
          }
        });
        const success = !!(await updateComment({
          commentId,
          content: comment,
          text: text,
          tags: tags,
        }));
        if (!success) {
          set((state) => {
            state.tasks[itemId] = ogTask;
          });
        }
        return success;
      },
      onAddFile: async ({ itemId, fileName, filePath }) => {
        set((state) => {
          const { tasks } = state;
          const task = tasks[itemId];
          if (task) {
            const files = task.attachments || [];

            files.push({ name: fileName, url: filePath });
            task.attachments = files;
          }
        });
        const success = !!(await addFile(itemId, fileName, filePath));
        if (!success) {
          set((state) => {
            const { tasks } = state;
            const task = tasks[itemId];
            if (task) {
              const files = task.attachments || [];
              const fileIndex = files.findIndex((file) => file.url === filePath);
              if (fileIndex > -1) {
                files.splice(fileIndex, 1);
                task.attachments = files;
              }
            }
          });
        }

        return success;
      },
      toggleWatchTask: async ({ taskId, watching }) => {
        const ogTask = get().tasks[taskId];
        if (!ogTask) return false;
        const updatedTask = await toggleWatchTaskApi(taskId, watching);
        const success = !!updatedTask;
        if (success) {
          set((state) => {
            state.tasks[taskId] = {
              ...ogTask,
              watch: updatedTask.watch,
              watching: updatedTask.watching,
            };
          });
        }
        return success;
      },
      onRemoveFile: async ({ itemId, filePath }) => {
        const fileObj = get().tasks[itemId]?.attachments?.find((file) => file.url === filePath);
        if (!fileObj) return false;
        set((state) => {
          const { tasks } = state;
          const task = tasks[itemId];
          if (task) {
            const files = task.attachments || [];
            const fileIndex = files.findIndex((file) => file.url === filePath);
            if (fileIndex > -1) {
              files.splice(fileIndex, 1);
              task.attachments = files;
            }
          }
        });
        const success = !!(await removeFile(itemId, filePath));
        if (!success) {
          set((state) => {
            const { tasks } = state;
            const task = tasks[itemId];
            if (task) {
              const files = task.attachments || [];
              files.push(fileObj);
              task.attachments = files;
            }
          });
        }

        return success;
      },

      onShareTask: async ({ access, itemId, members }) => {
        const ogTask = get().tasks[itemId];
        const sharedWithAccessObj = ogTask?.sharedWith;
        set((state) => {
          const { tasks } = state;
          const task = tasks[itemId];
          if (task) {
            const sharedMembers = members.map((item) => ({
              member: item,
              access: access,
            }));
            task.sharedWith = [...(task.sharedWith || []), ...sharedMembers];
          }
        });
        const success = !!(await shareTask(itemId, access, members, 'add'));
        if (!success) {
          set((state) => {
            const { tasks } = state;
            const task = tasks[itemId];
            if (task) {
              task.sharedWith = sharedWithAccessObj;
            }
          });
        }
        return success;
      },
      onChangeTaskShareAccess: async ({ access, itemId, member: memberAccessObj }) => {
        const ogTask = get().tasks[itemId];
        if (!ogTask) return false;
        set((state) => {
          const { tasks } = state;
          const task = tasks[itemId];
          if (task) {
            const sharedWith = [...(task.sharedWith || [])];
            task.sharedWith = sharedWith.map((item) => {
              if (item.member.memberId === memberAccessObj.member.memberId) {
                item.access = access;
              }
              return item;
            });
          }
        });
        const success = !!(await shareTask(itemId, access, memberAccessObj, 'edit'));
        if (!success) {
          set((state) => {
            const { tasks } = state;
            const task = tasks[itemId];
            if (task) {
              task.sharedWith = ogTask.sharedWith;
            }
          });
        }
        return success;
      },
      onRemoveTaskShareAccess: async ({ itemId, member: memberAccessObj }) => {
        const ogTask = get().tasks[itemId];
        if (!ogTask) return false;
        set((state) => {
          const { tasks } = state;
          const task = tasks[itemId];
          if (task) {
            task.sharedWith = [
              ...(task.sharedWith?.filter(
                (item) => item.member.memberId !== memberAccessObj.member.memberId
              ) || []),
            ];
          }
        });
        const success = !!(await shareTask(itemId, '', memberAccessObj, 'remove'));
        if (!success) {
          set((state) => {
            const { tasks } = state;
            const task = tasks[itemId];
            if (task) {
              task.sharedWith = ogTask.sharedWith;
            }
          });
        }
        return success;
      },

      duplicateTaskTemplate: async (id: string) => {
        const tasktemplate = await duplicateTaskTemplate(id);

        tasktemplate && get().setTask(tasktemplate.id, tasktemplate);
      },

      onSaveTemplate: async (templateName, customFields, taskId) => {
        const customFieldIds = customFields.map((field) => {
          const result = {};
          result[field.id] = field.isCopy;
          return result;
        });
        const template = await saveTemplate(templateName, customFieldIds, taskId);
        template && template.id && get().setTask(template.id, template);
        return template;
      },

      onImportTemplate: async (taskTemplate: any, taskId: string) => {
        set(async (state) => {
          const { tasks } = state;
          const task = tasks[taskId];
          if (task) {
            taskTemplate?.fields?.map((field) => {
              task.fields?.push(field);
            });
          }
        });
        const item = await importTemplate(taskTemplate.id, taskId);
        if (!item) return;
        item && get().setTask(taskId, item);
      },

      onUpdateTemplate: async (templateId: string, templateName?: any) => {
        set((state) => {
          const { tasks } = state;
          const template = tasks[templateId];
          if (template) {
            template.name = templateName;
          }
        });
        const item = await updateTemplate(templateId, templateName);
      },

      startTimer: (taskId: string) => {
        set((state) => {
          const { tasks } = state;
          const task = tasks[taskId];
          if (task && task.timer) {
            task.timer.startTime = new Date().toISOString();
            task.timer.isRunning = true;
          } else if (task) {
            task.timer = {
              startTime: new Date().toISOString(),
              stopTime: '',
              isRunning: true,
              billable: false,
              startDate: new Date().toISOString(),
            };
          }
        });
      },
      changeSubtaskParent: async (task: any, newParentTask: any) => {
        const ogTask = get().tasks[task.id];
        if (!ogTask) return false;
        set((state) => {
          if (state.tasks[task.id])
            state.tasks[task.id] = {
              ...task,
              parent: {
                id: newParentTask.id,
                _id: newParentTask.id,
                name: newParentTask.name,
                board: newParentTask.board,
              },
            };
        });

        const success = !!(await updateSubtaskParent(task.id, newParentTask._id));
        if (!success) {
          set((state) => {
            if (state.tasks[task.id]) state.tasks[task.id] = ogTask;
          });
        }
        return success;
      },
      onUpdateTaskColumn: async ({ taskId, columnId }) => {
        const task = get().tasks[taskId];
        if (!task) return false;
        set((state) => {
          if (state.tasks[taskId]) state.tasks[taskId] = { ...task, column: columnId };
        });
        useTableStore.getState().onUpdateItemPlacementState({
          itemId: taskId,
          destColId: columnId,
          destIndex: 0,
          sourceColId: task?.column || '',
        });
        const success = !!(await useKanbanStore.getState().onUpdateItemPlacement({
          itemId: taskId,
          destColId: columnId,
          destIndex: 0,
          boardId: task?.board?.id,
          sourceColId: task?.column || '',
        }));
        if (!success) {
          set((state) => {
            if (state.tasks[taskId]) state.tasks[taskId] = { ...task, column: task.column };
          });
        }
        return success;
      },

      ...taskApprovalSlice(set, get),
    }))
  )
);
